import { CircleHelp } from 'lucide-react'
import { ActionList, Popover, useBreakpoints } from '@shopify/polaris'
import type { ReactNode } from 'react'
import { useCallback, useMemo, useState } from 'react'
import type { HelpLauncherItem } from './types'
import styles from './HelpLauncher.module.scss'

type HelpLauncherProps = {
  items: HelpLauncherItem[]
  onActivatorClick?: () => void
  renderVirtualToursGroup?: (closePopover: () => void) => ReactNode
}

export const HelpLauncherShortcut = ({ keys }: { keys: string[] }) => {
  return (
    <span className={styles.shortcutHint}>
      {keys.map(key => (
        <span key={key} className={styles.shortcutKey}>
          {key}
        </span>
      ))}
    </span>
  )
}

export const HelpLauncher = ({
  items,
  onActivatorClick,
  renderVirtualToursGroup,
}: HelpLauncherProps) => {
  const { smDown: isMobile } = useBreakpoints()
  const [active, setActive] = useState(false)

  const toggleActive = useCallback(() => {
    onActivatorClick?.()
    setActive(previous => !previous)
  }, [onActivatorClick])

  const closePopover = useCallback(() => {
    setActive(false)
  }, [])

  const visibleItems = useMemo(() => items.filter(item => !item.hidden), [items])

  const mapActionItem = useCallback(
    (item: HelpLauncherItem) => ({
      content: item.label,
      prefix: item.prefix,
      suffix: item.suffix,
      onAction: () => {
        closePopover()
        requestAnimationFrame(() => {
          item.onAction()
        })
      },
    }),
    [closePopover],
  )

  const actionItems = useMemo(
    () =>
      visibleItems.length > 1 ? visibleItems.slice(0, -1).map(mapActionItem) : visibleItems.map(mapActionItem),
    [mapActionItem, visibleItems],
  )

  const footerItem = useMemo(
    () => (visibleItems.length > 1 ? mapActionItem(visibleItems[visibleItems.length - 1]) : null),
    [mapActionItem, visibleItems],
  )

  const virtualToursGroup = renderVirtualToursGroup?.(closePopover)

  const activator = (
    <div className={styles.launcher}>
      <button
        type="button"
        className={styles.launcherButton}
        onClick={toggleActive}
        aria-label="Open help menu"
      >
        <CircleHelp size={20} strokeWidth={1.8} />
      </button>
    </div>
  )

  if (isMobile || (!visibleItems.length && !virtualToursGroup)) {
    return null
  }

  return (
    <Popover
      active={active}
      activator={activator}
      autofocusTarget="first-node"
      preferredAlignment="left"
      preferredPosition="above"
      onClose={closePopover}
    >
      <div className={styles.popoverPane}>
        {virtualToursGroup}
        {visibleItems.length > 0 ? (
          <>
            <ActionList actionRole="menuitem" items={actionItems} />
            {footerItem ? (
              <div className={styles.footerSection}>
                <ActionList actionRole="menuitem" items={[footerItem]} />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Popover>
  )
}
